import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { Token } from 'src/app/_models/authws/authws';
import { UserInfo } from '../models/user';
import { environment } from 'src/environments/environment';

@Injectable()
export class SSOErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<UserInfo> =
    new BehaviorSubject<UserInfo>(null);
  constructor(private authenticationService: AuthenticationService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.params.has('token')) {
      return next.handle(request);
    }
    return next.handle(request).pipe(
      catchError((err) => {
        if (
          request.url.endsWith('/v2/security/authenticate') ||
          request.url.startsWith(environment.daUrl)
        ) {
          // intercept authenticate errors
          return this.handleAuthError(request, err);
        }
        // intercept authenticate errors
        //handle 401 and 403 error only for  daUrl
        if (
          request.url.includes(environment.daUrl) &&
          (err.status === 401 || err.status === 403)
        ) {
          return this.handle401Error(request, next);
        }
        if (request.url.includes('routing/v1/routingprofiles/')) {
          return this.handleRoutingProfileError(request, err);
        }
        return throwError(err);
      })
    );
  }

  handleAuthError(request: any, err: any): Observable<never> {
    console.error('SSOError:', err, request.url);
    const multiAccountsMsg = 'FailedToLoginDueToMultipleAccounts';
    if (
      err?.status === 400 &&
      err?.error &&
      err?.error?.status === multiAccountsMsg
    ) {
      const { accounts } = err.error;
      // accounts.unshift({ accountName: '-- select account --' });
      localStorage.setItem('_ssoAccounts', JSON.stringify(accounts));
    }

    if (err?.status === 500) {
      this.authenticationService.logout(); // clear a bad session
    }

    let reason = null; // for incorrect password
    if (err?.error?.reason) {
      reason = err.error.reason;
    }

    if (err.status === 401) {
      return throwError(err);
    }

    const error = err?.error?.message || reason || err?.statusText;
    return throwError(error);
  }

  private handleRoutingProfileError(request: any, err: any): Observable<any> {
    // Todo-yz: additional logging?
    // transforming error for user consumption.
    const error = new Error(err?.message);
    return throwError(error);
  }

  handle401Error(request: any, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const userInfo: UserInfo = JSON.parse(
        localStorage.getItem('currentUser')
      );
      if (userInfo?._jwtToken?.refresh_token) {
        return this.authenticationService.refreshToken(userInfo).pipe(
          switchMap((user: UserInfo) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(user);
            return next.handle(this.addTokenHeader(request, user?._jwtToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.authenticationService.logout();
            return throwError(err);
          }),
          finalize(() => (this.isRefreshing = false))
        );
      }
    }
    return this.refreshTokenSubject.pipe(
      filter((newtoken) => newtoken !== null),
      take(1),
      switchMap((user) =>
        next.handle(this.addTokenHeader(request, user?._jwtToken))
      )
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: Token) {
    return request.clone({
      headers: request.headers.set(
        'Authorization',
        `${token.token_type} ${token.access_token}`
      ),
    });
  }
}
